import Axios, { AxiosRequestConfig } from 'axios';
import GlobalConfig from './GlobalConfig';
import { UserLanguageService } from '../services';
import axiosRetry from 'axios-retry';

const nonRetryableStatuses = new Set([400, 401, 403, 404]);
const publicAxios = Axios.create();

axiosRetry(publicAxios, {
  retries: 3,
  retryDelay: (retryCount) => retryCount * 2000,
  retryCondition: (error) => {
    const status = error?.response?.status;
    if (status && nonRetryableStatuses.has(status)) {
      return false;
    }

    return axiosRetry.isNetworkOrIdempotentRequestError(error);
  },
});

const PublicApiProvider = {
  request: (
    isJsonRequest: boolean = true,
    accessToken?: string,
    turnstileToken?: string,
    paymentResultToken?: string
  ): AxiosRequestConfig => {
    const LanguageCodeHeader = 'LanguageCode';
    const currentLanguageCode = UserLanguageService.getLanguageCode();

    return {
      baseURL: GlobalConfig.val().PublicApiUrl,
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        ...(accessToken ? { 'X-Case-Access-Token': accessToken } : {}),
        ...(turnstileToken ? { 'cf-turnstile-response': turnstileToken } : {}),
        ...(paymentResultToken ? { 'X-Case-Payment-Return-Token': paymentResultToken } : {}),
      },
      transformRequest: (data: any, headers: any) => {
        if (currentLanguageCode.length) {
          headers[LanguageCodeHeader] = currentLanguageCode;
        }

        if (isJsonRequest) {
          return JSON.stringify(data);
        }

        return data;
      },
    };
  },

  get: async <T extends {}>(
    url: string,
    cancelToken?: any,
    accessToken?: string,
    paymentResultToken?: string
  ): Promise<T> => {
    let config = PublicApiProvider.request(true, accessToken, undefined, paymentResultToken);

    if (cancelToken) {
      config = { ...config, cancelToken };
    }

    const response = await publicAxios.get<T>(url, config);
    return response.data;
  },

  post: async <T extends {}>(
    url: string,
    body?: any,
    turnstileToken?: string,
    accessToken?: string,
    paymentResultToken?: string
  ): Promise<T> => {
    const config = PublicApiProvider.request(true, accessToken, turnstileToken, paymentResultToken);
    const response = await publicAxios.post<T>(url, body ?? null, config);
    return response.data;
  },

  postWithPaymentToken: async <T extends {}>(url: string, body?: any, paymentResultToken?: string): Promise<T> => {
    return PublicApiProvider.post<T>(url, body, undefined, undefined, paymentResultToken);
  },
};

export default PublicApiProvider;
